import { useEffect, useState, useCallback } from 'react';
import { TrendingUp, TrendingDown, AlertCircle, Download, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  MetricCard,
  KPIGrid,
  StatusBadge,
} from '@/components/common';
import {
  consumptionApi,
  VarianceAnalysisParams,
  VarianceAnalysisResponse,
  VarianceAnalysisRecord,
} from '@/api/services/consumption.api';
import { inventoryApi, InventoryItem, locationsApi, Location } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { formatDate, formatDateTime } from '@/lib/utils';

/**
 * @param embedded rendered as a tab inside Consumption rather than as a page of
 *   its own, so it drops its own header and keeps only its toolbar. The module
 *   used to be four pages linked by buttons in each other's headers; it is one
 *   page with four tabs now.
 */
export default function ConsumptionVarianceAnalysis({ embedded = false }: { embedded?: boolean }) {
  const [data, setData] = useState<VarianceAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [itemFilter, setItemFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [thresholdFilter, setThresholdFilter] = useState<string>('');

  // Reference data
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(false);

  const fetchReferenceData = async () => {
    try {
      setLoadingRefs(true);
      const [itemsRes, locationsRes] = await Promise.all([
        inventoryApi.getAll({ limit: 500 }),
        locationsApi.getAll({ limit: 500 }),
      ]);
      setInventoryItems(itemsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Failed to load reference data:', error);
    } finally {
      setLoadingRefs(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params: VarianceAnalysisParams = {};

      if (startDate) params.startDate = new Date(startDate).toISOString();
      if (endDate) params.endDate = new Date(endDate).toISOString();
      if (itemFilter !== 'all') params.itemId = itemFilter;
      if (locationFilter !== 'all') params.locationId = locationFilter;
      if (thresholdFilter) params.threshold = parseFloat(thresholdFilter);

      try {
        const response = await consumptionApi.getVarianceAnalysis(params);
        setData(response.data);
      } catch (apiError) {
        // Fallback to mock data if API is not available
        console.log('API not available, using mock data');

        const createMockRecord = (
          partial: Partial<VarianceAnalysisRecord>
        ): VarianceAnalysisRecord =>
          ({
            companyId: 'company-1',
            itemId: partial.item?.id || '',
            locationId: partial.location?.id || '',
            workOrderId: null,
            workOrder: null,
            approvedById: null,
            approvedBy: null,
            approvedAt: null,
            consumptionRate: null,
            rateUnit: null,
            baseMetric: null,
            certificateNumbers: [],
            requiresCertification: false,
            consumedById: 'user-1',
            consumedBy: { id: 'user-1', fullName: 'User', email: 'user@example.com' },
            createdAt: partial.consumedAt || new Date().toISOString(),
            updatedAt: partial.consumedAt || new Date().toISOString(),
            deletedAt: null,
            deletedBy: null,
            notes: null,
            ...partial,
          }) as VarianceAnalysisRecord;

        const mockData: VarianceAnalysisResponse = {
          records: [
            createMockRecord({
              id: '1',
              consumedAt: new Date('2026-01-20T10:30:00').toISOString(),
              item: {
                id: 'item-1',
                name: 'Drilling Fluid',
                sku: 'DF-001',
                category: 'Fluids',
                unit: 'BBL',
              },
              location: { id: 'loc-1', name: 'Rig Site A', code: 'RSA' },
              plannedQuantity: 100,
              quantity: 115,
              unit: 'BBL',
              variancePercentage: 15.0,
              varianceReason: 'FIELD_CONDITION_CHANGE',
              unitCost: 500,
              totalCost: 57500,
              jobName: 'Well-001',
              jobId: 'WELL-001',
              operationPhase: 'DRILLING',
              approvalStatus: 'PENDING',
              requiresApproval: true,
            }),
            createMockRecord({
              id: '2',
              consumedAt: new Date('2026-01-19T14:15:00').toISOString(),
              item: {
                id: 'item-2',
                name: 'Barite',
                sku: 'BAR-001',
                category: 'Additives',
                unit: 'TON',
              },
              location: { id: 'loc-2', name: 'Rig Site B', code: 'RSB' },
              plannedQuantity: 50,
              quantity: 46,
              unit: 'TON',
              variancePercentage: -8.0,
              varianceReason: 'NORMAL_SHRINKAGE',
              unitCost: 300,
              totalCost: 13800,
              jobName: 'Well-002',
              jobId: 'WELL-002',
              operationPhase: 'DRILLING',
              approvalStatus: 'AUTO_APPROVED',
              requiresApproval: false,
            }),
            createMockRecord({
              id: '3',
              consumedAt: new Date('2026-01-18T09:45:00').toISOString(),
              item: {
                id: 'item-3',
                name: 'Drill Bits (PDC)',
                sku: 'DB-001',
                category: 'Tools',
                unit: 'EA',
              },
              location: { id: 'loc-1', name: 'Rig Site A', code: 'RSA' },
              plannedQuantity: 5,
              quantity: 6,
              unit: 'EA',
              variancePercentage: 20.0,
              varianceReason: 'EQUIPMENT_ISSUE',
              unitCost: 4500,
              totalCost: 27000,
              jobName: 'Well-001',
              jobId: 'WELL-001',
              operationPhase: 'DRILLING',
              approvalStatus: 'APPROVED',
              requiresApproval: true,
            }),
            createMockRecord({
              id: '4',
              consumedAt: new Date('2026-01-17T16:20:00').toISOString(),
              item: {
                id: 'item-4',
                name: 'Corrosion Inhibitor',
                sku: 'CI-001',
                category: 'Chemicals',
                unit: 'GAL',
              },
              location: { id: 'loc-3', name: 'Rig Site C', code: 'RSC' },
              plannedQuantity: 200,
              quantity: 215,
              unit: 'GAL',
              variancePercentage: 7.5,
              varianceReason: 'FIELD_CONDITION_CHANGE',
              unitCost: 75,
              totalCost: 16125,
              jobName: 'Well-003',
              jobId: 'WELL-003',
              operationPhase: 'COMPLETION',
              approvalStatus: 'AUTO_APPROVED',
              requiresApproval: false,
            }),
            createMockRecord({
              id: '5',
              consumedAt: new Date('2026-01-16T11:10:00').toISOString(),
              item: {
                id: 'item-5',
                name: 'Cement Slurry',
                sku: 'CS-001',
                category: 'Cement',
                unit: 'BBL',
              },
              location: { id: 'loc-2', name: 'Rig Site B', code: 'RSB' },
              plannedQuantity: 150,
              quantity: 172,
              unit: 'BBL',
              variancePercentage: 14.67,
              varianceReason: 'FIELD_CONDITION_CHANGE',
              unitCost: 450,
              totalCost: 77400,
              jobName: 'Well-002',
              jobId: 'WELL-002',
              operationPhase: 'COMPLETION',
              approvalStatus: 'PENDING',
              requiresApproval: true,
            }),
            createMockRecord({
              id: '6',
              consumedAt: new Date('2026-01-15T08:30:00').toISOString(),
              item: {
                id: 'item-6',
                name: 'Pipe Dope',
                sku: 'PD-001',
                category: 'Chemicals',
                unit: 'GAL',
              },
              location: { id: 'loc-1', name: 'Rig Site A', code: 'RSA' },
              plannedQuantity: 25,
              quantity: 24,
              unit: 'GAL',
              variancePercentage: -4.0,
              varianceReason: 'NORMAL_SHRINKAGE',
              unitCost: 120,
              totalCost: 2880,
              jobName: 'Well-001',
              jobId: 'WELL-001',
              operationPhase: 'DRILLING',
              approvalStatus: 'AUTO_APPROVED',
              requiresApproval: false,
            }),
          ],
          summary: {
            totalPlanned: 530,
            totalActual: 578,
            totalVariance: 48,
            averageVariancePercentage: 9.05,
            totalCostVariance: 24705,
            recordsWithVariance: 6,
            positiveVariances: 4,
            negativeVariances: 2,
          },
        };
        setData(mockData);
        toast.info('Showing mock data (API not available)');
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [endDate, itemFilter, locationFilter, startDate, thresholdFilter]);

  useEffect(() => {
    fetchReferenceData();
    fetchData();
  }, [fetchData]);

  const handleApplyFilters = () => {
    fetchData();
  };

  const handleExportCSV = () => {
    if (!data || data.records.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = [
      'Date',
      'Item',
      'SKU',
      'Location',
      'Planned Qty',
      'Actual Qty',
      'Variance %',
      'Variance Reason',
      'Unit Cost',
      'Total Cost',
      'Cost Variance',
      'Approval Status',
    ];

    const rows = data.records.map((record) => [
      formatDateTime(record.consumedAt),
      record.item.name,
      record.item.sku,
      record.location.name,
      record.plannedQuantity,
      record.quantity,
      record.variancePercentage?.toFixed(2),
      record.varianceReason || '',
      record.unitCost?.toFixed(2),
      record.totalCost?.toFixed(2),
      record.plannedQuantity && record.unitCost
        ? ((record.totalCost || 0) - record.plannedQuantity * record.unitCost).toFixed(2)
        : '0',
      record.approvalStatus,
    ]);

    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `variance-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success('Variance analysis exported successfully');
  };

  // Prepare chart data
  const trendData = data?.records
    .slice(0, 30)
    .reverse()
    .map((record) => ({
      date: formatDate(record.consumedAt),
      variance: record.variancePercentage || 0,
      item: record.item.name,
    }));

  const topVarianceItems = data?.records
    .reduce((acc: any[], record) => {
      const existing = acc.find((item) => item.itemId === record.itemId);
      if (existing) {
        existing.totalVariance += Math.abs(record.variancePercentage || 0);
        existing.costImpact +=
          record.plannedQuantity && record.unitCost
            ? Math.abs((record.totalCost || 0) - record.plannedQuantity * record.unitCost)
            : 0;
        existing.count += 1;
      } else {
        acc.push({
          itemId: record.itemId,
          name: record.item.name,
          totalVariance: Math.abs(record.variancePercentage || 0),
          costImpact:
            record.plannedQuantity && record.unitCost
              ? Math.abs((record.totalCost || 0) - record.plannedQuantity * record.unitCost)
              : 0,
          count: 1,
        });
      }
      return acc;
    }, [])
    .sort((a, b) => b.costImpact - a.costImpact)
    .slice(0, 10);

  const columns: Column<VarianceAnalysisRecord>[] = [
    {
      key: 'consumedAt',
      header: 'Date',
      sortable: true,
      render: (row: VarianceAnalysisRecord) => formatDate(row.consumedAt),
    },
    {
      key: 'item',
      header: 'Item',
      render: (row: VarianceAnalysisRecord) => (
        <div>
          <p className="font-medium">{row.item.name}</p>
          <p className="text-xs text-muted-foreground">{row.item.sku}</p>
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (row: VarianceAnalysisRecord) => row.location.name,
    },
    {
      key: 'planned',
      header: 'Planned',
      render: (row: VarianceAnalysisRecord) => `${row.plannedQuantity} ${row.unit}`,
    },
    {
      key: 'actual',
      header: 'Actual',
      render: (row: VarianceAnalysisRecord) => `${row.quantity} ${row.unit}`,
    },
    {
      key: 'variance',
      header: 'Variance',
      render: (row: VarianceAnalysisRecord) => (
        <span
          className={`font-medium ${
            row.variancePercentage && row.variancePercentage > 0
              ? 'text-status-destructive'
              : 'text-status-success'
          }`}
        >
          {row.variancePercentage && row.variancePercentage > 0 ? '+' : ''}
          {row.variancePercentage?.toFixed(1)}%
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row: VarianceAnalysisRecord) => (
        <span className="text-sm">{row.varianceReason || '-'}</span>
      ),
    },
    {
      key: 'costImpact',
      header: 'Cost Impact',
      render: (row: VarianceAnalysisRecord) => {
        if (!row.plannedQuantity || !row.unitCost) return '-';
        const plannedCost = row.plannedQuantity * row.unitCost;
        const costVariance = (row.totalCost || 0) - plannedCost;
        return (
          <span
            className={`font-medium ${
              costVariance > 0 ? 'text-status-destructive' : 'text-status-success'
            }`}
          >
            {costVariance > 0 ? '+' : ''}${costVariance.toFixed(2)}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row: VarianceAnalysisRecord) => (
        <StatusBadge
          status={
            row.approvalStatus === 'APPROVED' || row.approvalStatus === 'AUTO_APPROVED'
              ? 'approved'
              : row.approvalStatus === 'PENDING'
                ? 'pending'
                : 'rejected'
          }
          label={row.approvalStatus}
        />
      ),
    },
  ];

  return (
    <div className="space-y-5">
      {embedded ? (
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={handleExportCSV}
            disabled={!data || data.records.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      ) : (
        <PageHeader
          title="Variance Analysis"
          description="Planned against actual consumption by item and location, with the cost impact of each difference."
          actions={
            <Button onClick={handleExportCSV} disabled={!data || data.records.length === 0}>
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          }
        />
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <h3 className="text-base font-semibold text-foreground">Filters</h3>
          <CardDescription>Filter variance analysis data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="item">Item</Label>
              <Select value={itemFilter} onValueChange={setItemFilter} disabled={loadingRefs}>
                <SelectTrigger>
                  <SelectValue placeholder="All Items" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  {inventoryItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Select
                value={locationFilter}
                onValueChange={setLocationFilter}
                disabled={loadingRefs}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="threshold">Min Variance %</Label>
              <Input
                id="threshold"
                type="number"
                min="0"
                step="1"
                value={thresholdFilter}
                onChange={(e) => setThresholdFilter(e.target.value)}
                placeholder="e.g., 10"
              />
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={handleApplyFilters}>
              <Calendar className="mr-2 h-4 w-4" />
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary KPIs */}
      {data && (
        <KPIGrid columns={4}>
          <MetricCard
            label="Total Planned"
            value={`${data.summary.totalPlanned.toFixed(0)}`}
            sentiment="info"
            trend={{ direction: 'neutral', value: 'units planned' }}
          />
          <MetricCard
            label="Total Actual"
            value={`${data.summary.totalActual.toFixed(0)}`}
            sentiment={data.summary.totalVariance > 0 ? 'warning' : 'success'}
            trend={{
              direction: data.summary.totalVariance > 0 ? 'up' : 'down',
              value: `${data.summary.totalVariance > 0 ? '+' : ''}${data.summary.totalVariance.toFixed(0)} units variance`,
            }}
          />
          <MetricCard
            label="Avg Variance"
            value={`${data.summary.averageVariancePercentage.toFixed(1)}%`}
            sentiment={data.summary.averageVariancePercentage > 10 ? 'danger' : 'warning'}
            trend={{
              direction: data.summary.averageVariancePercentage > 0 ? 'up' : 'down',
              value: data.summary.averageVariancePercentage > 0 ? 'Over budget' : 'Under budget',
            }}
          />
          <MetricCard
            label="Cost Variance"
            value={`$${Math.abs(data.summary.totalCostVariance).toFixed(0)}`}
            sentiment={data.summary.totalCostVariance > 0 ? 'danger' : 'success'}
            trend={{
              direction: data.summary.totalCostVariance > 0 ? 'up' : 'down',
              value: data.summary.totalCostVariance > 0 ? 'Over budget' : 'Under budget',
            }}
          />
        </KPIGrid>
      )}

      {/* Charts */}
      {data && data.records.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Variance Trend Chart */}
          <Card>
            <CardHeader>
              <h3 className="text-base font-semibold text-foreground">
                Variance Trends (Last 30 Records)
              </h3>
              <CardDescription>Track variance percentage over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="variance"
                    stroke="#8884d8"
                    name="Variance %"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top Variance Items Chart */}
          <Card>
            <CardHeader>
              <h3 className="text-base font-semibold text-foreground">
                Top 10 Items by Cost Impact
              </h3>
              <CardDescription>Items with highest cost variance</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topVarianceItems} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="costImpact" fill="#ef4444" name="Cost Impact ($)" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Variance Statistics */}
      {data && (
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">Variance Statistics</h3>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-4 p-4 border rounded-lg dark:border-slate-700">
                <AlertCircle className="h-8 w-8 text-status-info" />
                <div>
                  <p className="text-sm text-muted-foreground">Records with Variance</p>
                  <p className="text-2xl font-bold">{data.summary.recordsWithVariance}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 border rounded-lg dark:border-slate-700">
                <TrendingUp className="h-8 w-8 text-status-destructive" />
                <div>
                  <p className="text-sm text-muted-foreground">Positive Variances (Over)</p>
                  <p className="text-2xl font-bold text-status-destructive">
                    {data.summary.positiveVariances}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 border rounded-lg dark:border-slate-700">
                <TrendingDown className="h-8 w-8 text-status-success" />
                <div>
                  <p className="text-sm text-muted-foreground">Negative Variances (Under)</p>
                  <p className="text-2xl font-bold text-status-success">
                    {data.summary.negativeVariances}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      {data && (
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">Variance Records</h3>
            <CardDescription>
              Detailed variance records ({data.records.length} total)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={data.records}
              loading={loading}
              pagination
              exportable
              columnToggle
              searchable
            />
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {data && data.records.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">No variance data found</p>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              Try adjusting your filters or ensure consumption records have planned quantities
              entered.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
